import { kreatorApi, superAdminApi } from './api';

/**
 * Adapter API używany w sekcjach Kreatora. Pozwala re-użyć te same komponenty
 * (KreatorSettingsSection, KreatorColorsSection itd.) zarówno w panelu sklepu
 * (tenant-scoped, integration-scoped) jak i w panelu super-admina (system-wide
 * defaults). Sekcja nie wie skąd dane przychodzą - po prostu woła adapter.
 *
 * - createTenantKreatorAdapter(integrationId): klasyczny tryb sklepu
 * - createSuperAdminKreatorAdapter(): edycja globalnych domyślnych
 */

export type ApiResult = { data: unknown };

export interface KreatorApiAdapter {
  updateSettings: (data: Record<string, unknown>) => Promise<ApiResult>;
  updateColors: (data: Record<string, unknown>) => Promise<ApiResult>;
  updateTypography: (data: Record<string, unknown>) => Promise<ApiResult>;
  updateNavigation: (data: Record<string, unknown>) => Promise<ApiResult>;
  updateFooter: (data: Record<string, unknown>) => Promise<ApiResult>;
  updatePageAppearance: (data: Record<string, unknown>) => Promise<ApiResult>;
  /** Czy ten adapter dotyczy konkretnego sklepu (true) czy globalnych defaults (false). */
  isTenantScoped: boolean;
  purgeWpCache: () => Promise<ApiResult>;
  diagnoseWp: () => Promise<{ data: { ok: boolean; error?: string } }>;
}

export function createTenantKreatorAdapter(integrationId: string): KreatorApiAdapter {
  return {
    isTenantScoped: true,
    updateSettings: (data) => kreatorApi.updateSettings(integrationId, data),
    updateColors: (data) => kreatorApi.updateColors(integrationId, data),
    updateTypography: (data) => kreatorApi.updateTypography(integrationId, data),
    updateNavigation: (data) => kreatorApi.updateNavigation(integrationId, data),
    updateFooter: (data) => kreatorApi.updateFooter(integrationId, data),
    updatePageAppearance: (data) => kreatorApi.updatePageAppearance(integrationId, data),
    purgeWpCache: () => kreatorApi.purgeWpCache(integrationId),
    diagnoseWp: () => kreatorApi.diagnoseWp(integrationId),
  };
}

const noopApiResult: ApiResult = { data: null };

/**
 * Super-admin adapter - zapisuje globalne defaulty przez
 * /api/super-admin/kreator-defaults/:section. Brak konkretnego WP sklepu,
 * więc purgeWpCache i diagnoseWp są no-op.
 */
export function createSuperAdminKreatorAdapter(): KreatorApiAdapter {
  return {
    isTenantScoped: false,
    updateSettings: (data) => superAdminApi.updateKreatorDefaultsSection('settings', data),
    updateColors: (data) => superAdminApi.updateKreatorDefaultsSection('colors', data),
    updateTypography: (data) => superAdminApi.updateKreatorDefaultsSection('typography', data),
    updateNavigation: (data) => superAdminApi.updateKreatorDefaultsSection('navigation', data),
    updateFooter: (data) => superAdminApi.updateKreatorDefaultsSection('footer', data),
    updatePageAppearance: (data) => superAdminApi.updateKreatorDefaultsSection('pageAppearance', data),
    purgeWpCache: async () => noopApiResult,
    diagnoseWp: async () => ({ data: { ok: true } }),
  };
}
